import {updateQrStatus} from "./slices/qr";
import {Dispatch} from "react";
import {UnknownAction} from "@reduxjs/toolkit";

type QrCodeMessage = {
  event: 'QR_READY',
  product_id: string,
  product_name: string,
  qr_code_id: string,
  remote_archive_paths: string[]
}

type QrBalanceMessage = {
  event: 'CHANGE_BALANCE',
  balance: number,
}

type HeartbeatMessage = {
  event: 'HEARTBEAT',
}

type SocketMessageData = QrCodeMessage | QrBalanceMessage | HeartbeatMessage

export class BestWebSocket {
  private readonly url: string;
  private socket: WebSocket | null = null;
  private dispatch: any;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10; // Увеличенное количество попыток
  private reconnectDelay: number = 2000; // Увеличено до 2 секунд
  private pingInterval: number | null = null;
  private pingTimeout: number = 30000; // 30 секунд таймаут
  public isConnected = false;

  constructor(url: string) {
    this.url = url;
  }

  // Инициализация WebSocket с доступом к Redux dispatch
  public init(dispatch: Dispatch<UnknownAction>): void {
    if (this.isConnected) {
      return;
    } else {
      this.dispatch = dispatch;
      this.connect();
    }
  }

  // Отправка пинга для поддержания соединения
  private startPingInterval() {
    // Очищаем предыдущий интервал если он был
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
    
    // Устанавливаем новый интервал для отправки пинга
    this.pingInterval = window.setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        try {
          console.log('Sending ping to keep connection alive');
          this.socket.send(JSON.stringify({ event: 'PING' }));
        } catch (error) {
          console.error('Error sending ping:', error);
        }
      }
    }, 20000) as unknown as number; // Пинг каждые 20 секунд
  }

  // Подключение к серверу
  private connect(): void {
    // Закрываем предыдущее соединение если оно существует
    if (this.socket) {
      try {
        this.socket.close();
      } catch (e) {
        console.error('Error closing existing socket:', e);
      }
    }

    try {
      console.log('Connecting to WebSocket server at', this.url);
      this.socket = new WebSocket(this.url);

      // Устанавливаем таймаут на соединение
      const connectionTimeout = setTimeout(() => {
        if (this.socket && this.socket.readyState !== WebSocket.OPEN) {
          console.warn('Connection timeout, closing socket');
          this.socket.close();
        }
      }, this.pingTimeout);

      this.socket.onopen = () => {
        clearTimeout(connectionTimeout);
        this.isConnected = true;
        console.log('Connected to server successfully');
        this.reconnectAttempts = 0;
        this.startPingInterval();
      };

      this.socket.onmessage = (event: MessageEvent) => {
        console.log('WebSocket received raw message:', event.data);
        try {
            const data: SocketMessageData = JSON.parse(event.data);
            
            // Handle heartbeat messages
            if (data.event === 'HEARTBEAT') {
                console.log('Received heartbeat from server');
                return;
            }
            
            // Handle QR_READY events with explicit acknowledgment
            if (data.event === 'QR_READY') {
                console.log('Processing QR_READY message with ID:', data.qr_code_id);
                try {
                    // Сохраняем копию необходимых данных
                    const qrInfo = {
                        qr_code_id: data.qr_code_id,
                        productId: data.product_id,
                        productName: data.product_name,
                        updatedFields: {
                            link: data.remote_archive_paths
                        }
                    };
                    
                    // Безопасно обрабатываем dispatch
                    if (this.dispatch) {
                        this.dispatch(updateQrStatus(qrInfo));
                        console.log('QR data dispatched to Redux store');
                    } else {
                        console.error('Dispatch function is not initialized');
                    }
                    
                    // Отправляем подтверждение отдельно от основного кода
                    setTimeout(() => {
                        try {
                            if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                                const ackMessage = JSON.stringify({
                                    event: 'QR_RECEIVED',
                                    qr_code_id: data.qr_code_id
                                });
                                console.log('Sending QR_RECEIVED acknowledgment:', ackMessage);
                                this.socket.send(ackMessage);
                                console.log('QR_RECEIVED acknowledgment sent successfully');
                            } else {
                                console.warn('Cannot send QR_RECEIVED: WebSocket is not open');
                            }
                        } catch (ackError) {
                            console.error('Error sending QR acknowledgment:', ackError);
                        }
                    }, 200); // Увеличиваем задержку до 200мс
                } catch (qrError) {
                    console.error('Error processing QR_READY message:', qrError);
                }
            }
        } catch (error) {
            console.error('Error parsing WebSocket message:', error, event.data);
        }
      };

      this.socket.onclose = (event) => {
        clearTimeout(connectionTimeout);
        if (this.pingInterval) {
          clearInterval(this.pingInterval);
          this.pingInterval = null;
        }
        
        this.isConnected = false;
        console.log(`Disconnected from server. Code: ${event.code}, Reason: ${event.reason || 'No reason provided'}, Clean: ${event.wasClean}`);
        
        // Пытаемся переподключиться только если закрытие было не чистым
        if (!event.wasClean) {
          this.attemptReconnect();
        }
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket error occurred:', error);
      };
    } catch (connectionError) {
      console.error('Error establishing WebSocket connection:', connectionError);
      this.attemptReconnect();
    }
  }

  // Отправка события
  public emit<T = any>(event: string, payload: T): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        const message = JSON.stringify({ event, payload });
        this.socket.send(message);
      } catch (error) {
        console.error('Error sending message:', error);
      }
    } else {
      console.warn('WebSocket is not open. Message not sent.');
      // Пробуем переподключиться, если сокет закрыт
      if (!this.isConnected) {
        this.connect();
      }
    }
  }

  // Автоматическое восстановление соединения
  private attemptReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * Math.pow(1.5, Math.min(this.reconnectAttempts - 1, 5)); // Экспоненциальная задержка с ограничением
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms...`);
      setTimeout(() => this.connect(), delay);
    } else {
      console.error('Max reconnection attempts reached. Giving up.');
    }
  }

  // Отключение
  public disconnect(): void {
    console.log('Disconnecting socket');
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.socket) {
      this.socket.close(1000, 'Closed by client');
      this.socket = null;
    }
    this.isConnected = false;
  }
}